/**
 * Small component rendered above the scroll area - contains the logo,
 * the provider name, the close button, hides it's text and animates the logo
 * to the center for IA mode.
 */

import React, { useEffect } from "react";
import { connect, useSelector } from "react-redux";
import styled from "styled-components";
import { getAdvisorName, getChatMode, getCommunityName, getIconLogo, getLatestState } from "../../store";

import { ArrowRoundBorders, CrossCloseIcon } from "../../lib/icons";
import { UserEvents, UserEventsAPI } from "../../lib/userEventsAPI";
import { ChatModes } from "../../lib/types";
import { deepChange, goToModule, rememberTeaserClosed, stopTitleFlashing } from "../../actions";
import { getIsIAactive } from "../InstantAnswers/selectors";
import { trackLinkClick, trackTextSelection } from "./actions";
import { shouldShowGallery } from "../Gallery/selectors";
import { closeGallery } from "../Gallery/actions";
import { clearIAQueue } from "../InstantAnswers/actions";

/**
 * Make the chat smaller than the current state.
 */
const makeChatSmaller = (dispatch, getState) => {
  const chatMode = getChatMode(getState());
  dispatch(stopTitleFlashing());
  let newChatMode = chatMode;
  switch (chatMode) {
    case ChatModes.teaser:
      newChatMode = ChatModes.long;
      break;
    case ChatModes.long:
      newChatMode = ChatModes.circle;
      break;
    case ChatModes.open:
      newChatMode = ChatModes.long;
      break;
    default:
  }
  dispatch(stopTitleFlashing());
  if (chatMode !== newChatMode) {
    if (chatMode !== ChatModes.open) {
      rememberTeaserClosed(newChatMode);
    }
    dispatch(deepChange({ chatMode: newChatMode }));
    dispatch(UserEventsAPI.send(UserEvents.CHAT_CLOSE, { downTo: newChatMode }));
  }
};

const ChatHeader = ({ iconLogo, communityName, advisorName, isIAactive, chatMode, dispatch }) => {
  useEffect(() => {
    window.addEventListener(
      "keydown",
      (e) => {
        if (e.key === "Escape" || e.key === "Esc" || e.keyCode === 27) {
          const state = dispatch(getLatestState);
          e.preventDefault();
          if (shouldShowGallery(state)) {
            dispatch(closeGallery());
          } else {
            dispatch(makeChatSmaller);
          }
        }
      },
      true
    );

    // Well this can be placed in some other component as well.
    window.addEventListener(
      "beforeunload",
      function () {
        dispatch(UserEventsAPI.sendPageClosed());
      },
      false
    );

    document.addEventListener("selectionchange", () => dispatch(trackTextSelection));
    document.addEventListener("click", (e) => dispatch(trackLinkClick(e)));
  }, []); // eslint-disable-line

  /**
   * Get back from IA mode.
   */
  const deactivateIA = () => {
    dispatch(clearIAQueue());
    dispatch(goToModule(1));
  };

  const _makeChatSmaller = (e) => {
    e && e.stopPropagation();
    dispatch(makeChatSmaller);
  };

  let className = chatMode;
  if (isIAactive) {
    className += " ia";
  }

  // TODO: move controls and title style modifiers away from Chat.js.
  return (
    <HeaderDiv className={className}>
      <div className="controls left">
        <div className="back-area" onClick={deactivateIA}>
          <ArrowRoundBorders className="arrow left" />
          <span>Back</span>
        </div>
      </div>

      <div className="before-curtain" />

      <HeaderLogo iconLogo={iconLogo} />

      <div className="title">
        {advisorName} <span className="link">{communityName}</span>
      </div>

      <div className="after-curtain" />

      <div className="controls">
        <div className="close-area" onClick={_makeChatSmaller}>
          <CrossCloseIcon />
        </div>
      </div>
    </HeaderDiv>
  );
};

/**
 * Circle logo.
 */
export const HeaderLogo = styled.div`
  display: inline-block;
  position: relative;
  height: 55px;
  width: 55px;

  border-radius: 50%;

  background-image: url(${(props) => props.iconLogo || ""});
  background-color: ${(props) => props.theme.buttonColor || "#ffffff"};
  background-repeat: no-repeat;
  background-position: 50%;
  background-size: 70%;
  box-shadow: #dcd8d8 0 0 6px 1px;

  &:after {
    content: "";
    display: block;
    position: absolute;

    background-color: #18dc72;
    width: 7px;
    height: 7px;
    border-radius: 7px;

    right: 8px;
    top: 2px;
  }
`;

export const HeaderDiv = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  padding: 8px;
  width: 100%;
  //justify-content: center;

  .before-curtain,
  .after-curtain {
    width: 0;
    background-color: white;
    opacity: 20;
    transition: width 0.3s ease-out, opacity 0.3s ease-out;
  }

  .after-curtain {
    position: absolute;
    right: 20px;
    top: 0;
    // Some ugly dances to make this curtain invisible upon the IA closing into the long control bar state.
    height: 60px;
    border-bottom-right-radius: 99px;
  }

  &.ia.open {
    .before-curtain {
      width: calc(50% - 27px);
    }

    .after-curtain {
      width: calc(50% - 35px - 20px);
      opacity: 100;
    }

    .title {
      left: calc(50% + 35px);
      color: white;

      * {
        color: white;
      }
    }
    .controls.left {
      opacity: 100;
    }
  }
`;

function mapStateToProps(state) {
  return {
    communityName: getCommunityName(state),
    advisorName: getAdvisorName(state),
    iconLogo: getIconLogo(state),
    isIAactive: getIsIAactive(state),
    chatMode: getChatMode(state),
  };
}

export default connect(mapStateToProps)(ChatHeader);
